import React from "react";
import { useTranslation } from 'react-i18next';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';
import AppIcon from '../../../../components/AppIcon';

interface Filters {
  values?: string;
  department?: string;
  timeRange?: string;
}

interface FilterBarProps {
  onFilterChange: (filters: Filters) => void;
  activeFilters: Filters;
  totalCount?: number;
  departments?: Array<{ value: string; label: string }>;
  companyValues?: Array<{ value: string; label: string }>;
}

const FilterBar: React.FC<FilterBarProps> = ({
  onFilterChange = () => {},
  activeFilters = {},
  totalCount = 0,
  departments = [],
  companyValues = [],
}) => {
  const { t } = useTranslation('performance');

  const valueOptions = companyValues.length > 0
    ? [{ value: "all", label: t('recognition.allValues') }, ...companyValues]
    : [
        { value: "all", label: t('recognition.allValues') },
        { value: "Innovation", label: t('recognition.valueInnovation') },
        { value: "Collaboration", label: t('recognition.valueCollaboration') },
        { value: "Excellence", label: t('recognition.valueExcellence') },
        { value: "Integrity", label: t('recognition.valueIntegrity') },
        { value: "Customer Focus", label: t('recognition.valueCustomerFocus') },
        { value: "Growth Mindset", label: t('recognition.valueGrowthMindset') },
      ];

  const departmentOptions = departments.length > 0
    ? [{ value: "all", label: t('recognition.allDepartments') }, ...departments]
    : [
        { value: "all", label: t('recognition.allDepartments') },
        { value: "Engineering", label: t('recognition.deptEngineering') },
        { value: "Marketing", label: t('recognition.deptMarketing') },
        { value: "Sales", label: t('recognition.deptSales') },
        { value: "HR", label: t('recognition.deptHR') },
        { value: "Design", label: t('recognition.deptDesign') },
        { value: "Product", label: t('recognition.deptProduct') },
        { value: "Finance", label: t('recognition.deptFinance') },
      ];

  const timeRanges = [
    { value: "today", label: t('recognition.timeToday') },
    { value: "week", label: t('recognition.timeThisWeek') },
    { value: "month", label: t('recognition.timeThisMonth') },
    { value: "quarter", label: t('recognition.timeThisQuarter') },
    { value: "all", label: t('recognition.timeAllTime') },
  ];

  const handleFilterChange = (filterType: keyof Filters, value: any) => {
    const updated: Filters = {
      ...activeFilters,
      [filterType]: value,
    };
    onFilterChange(updated);
  };

  const clearAllFilters = () => {
    onFilterChange({
      values: "all",
      department: "all",
      timeRange: "all",
    });
  };

  const hasActiveFilters = Object.values(activeFilters).some(
    (filter) => filter && filter !== "all"
  );

  return (
    <div className="border border-border rounded-lg p-3 mb-4 max-w-3xl mx-auto w-full relative">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AppIcon name="Filter" size={20} className="text-muted-foreground" />
          <h3 className="text-lg font-medium text-foreground">
            {t('recognition.filters')}
          </h3>
          {totalCount > 0 && (
            <span className="text-sm text-muted-foreground ms-2">
              ({totalCount} {totalCount !== 1 ? t('recognition.recognitionsPlural') : t('recognition.recognitionSingular')})
            </span>
          )}
        </div>
      </div>

      {/* Filter Controls Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 max-w-3xl mx-auto">
        <Select
          options={valueOptions}
          value={activeFilters.values || "all"}
          onChange={(val: string) => handleFilterChange("values", val)}
          placeholder={t('recognition.valuesPlaceholder')}
        />

        <Select
          options={departmentOptions}
          value={activeFilters.department || "all"}
          onChange={(val: string) => handleFilterChange("department", val)}
          placeholder={t('recognition.departmentPlaceholder')}
        />

        <Select
          options={timeRanges}
          value={activeFilters.timeRange || "week"}
          onChange={(val: string) => handleFilterChange("timeRange", val)}
          placeholder={t('recognition.timeRangePlaceholder')}
        />
      </div>

      {/* Clear All Button */}
      {hasActiveFilters && (
        <div className="flex justify-end mt-3 pt-2 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={clearAllFilters}
          >
            <AppIcon name="X" size={14} className="me-1" />
            {t('recognition.clearAllFilters')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;